import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { memo, type ReactNode, useEffect } from "react";
import { SUPPLIES_PAYMENT_METHOD_REPLAY_BLOCK_SELECTOR } from "../setting/billing/payment-methods/supplies-session-replay-privacy";

type SessionReplayTracking = Parameters<typeof amplitude.add>[0] & {
  name: string;
};

let suppliesSessionReplayTracking: SessionReplayTracking | null = null;
let suppliesSessionReplayAddPromise: Promise<unknown> | null = null;

function canTrackSuppliesSessionReplay(): boolean {
  return (
    typeof window !== "undefined" &&
    process.env.NODE_ENV !== "development" &&
    globalThis.REDO_MOCK_MODE !== "true" &&
    !!globalThis.AMPLITUDE_API_KEY
  );
}

function getSuppliesSessionReplayTracking(): SessionReplayTracking {
  suppliesSessionReplayTracking ??= sessionReplayPlugin({
    forceSessionTracking: true,
    privacyConfig: {
      blockSelector: [SUPPLIES_PAYMENT_METHOD_REPLAY_BLOCK_SELECTOR],
      defaultMaskLevel: "medium",
      maskSelector: [".amp-mask"],
    },
    sampleRate: 1,
  }) as SessionReplayTracking;
  return suppliesSessionReplayTracking;
}

function enableSuppliesSessionReplay() {
  const sessionReplayTracking = getSuppliesSessionReplayTracking();
  suppliesSessionReplayAddPromise = amplitude
    .add(sessionReplayTracking)
    .promise.catch(() => undefined);
}

function disableSuppliesSessionReplay() {
  const sessionReplayTracking = suppliesSessionReplayTracking;
  if (!sessionReplayTracking) {
    return;
  }
  void (suppliesSessionReplayAddPromise ?? Promise.resolve())
    .then(() => amplitude.remove(sessionReplayTracking.name).promise)
    .catch(() => undefined)
    .finally(() => {
      suppliesSessionReplayAddPromise = null;
    });
}

export const RedoSuppliesSessionReplayProvider = memo(
  function RedoSuppliesSessionReplayProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    useEffect(() => {
      if (!canTrackSuppliesSessionReplay()) {
        return;
      }
      enableSuppliesSessionReplay();
      return disableSuppliesSessionReplay;
    }, []);

    return <>{children}</>;
  },
);
